import { formatPrice } from 'utils/helpers';
import { shortRange } from 'utils/time-off';
import { Roles, BadgeColor, BackOfficeRoles } from 'types';
import {
  BookingRow,
  BookingStatus,
  BookingCancelReason,
  BookingPaymentTransaction,
} from 'store/api/booking/types';

// BOOKING (owner 25 Oct 2026): what the Booking screens share.

/** Who uses Booking (owner: "booking, administrative, owner"). */
export const BOOKING_ROLES: string[] = [
  Roles.COMPANY_OWNER,
  BackOfficeRoles.ADMINISTRATIVE,
  BackOfficeRoles.RESEARCH_MANAGER,
  BackOfficeRoles.RESEARCH_REP,
];

export const isBookingRole = (role?: string | null) =>
  Boolean(role && BOOKING_ROLES.includes(role));

/**
 * Last year's sales and profit are the P&L's numbers: only the Booking roles
 * that read the P&L in full see them (the server checks the same).
 */
export const canReadLastYear = (role?: string | null) =>
  role === Roles.COMPANY_OWNER || role === BackOfficeRoles.ADMINISTRATIVE;

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  booked: 'Booked',
  applied: 'Applied',
  accepted: 'Accepted',
  // Stored 'approved': on the list, not applied yet (owner 26 Oct 2026).
  approved: 'To apply',
  canceled: 'Canceled',
  needs_funding: 'Needs Funding',
};

// Today's chip colours, closest to the design's muted ones: To apply soft
// rose, Applied light cerulean, Accepted yellow (the booth is owed), Needs
// Funding lavender, Booked green, Canceled grey.
export const BOOKING_STATUS_COLOR: Record<BookingStatus, BadgeColor> = {
  booked: BadgeColor.Green,
  approved: BadgeColor.Pink,
  canceled: BadgeColor.LightGrey,
  accepted: BadgeColor.SecondaryYellow,
  applied: BadgeColor.SecondaryCerulean,
  needs_funding: BadgeColor.SecondaryPigmentIndigo,
};

// Why a show was canceled (owner 26 Oct 2026: "Pass by Owner, Declined by
// Show", plus Other with a short note).
export const CANCEL_REASON_LABEL: Record<BookingCancelReason, string> = {
  other: 'Other',
  passed_by_owner: 'Passed by owner',
  declined_by_show: 'Declined by show',
};

export const CANCEL_REASON_OPTIONS = (
  ['passed_by_owner', 'declined_by_show', 'other'] as BookingCancelReason[]
).map((value) => ({ value, label: CANCEL_REASON_LABEL[value] }));

/** "Declined by show", "Other: venue closed", or null. */
export const cancelReasonText = (
  row: Partial<Pick<BookingRow, 'cancelNote' | 'cancelReason'>>
) => {
  if (!row.cancelReason) {
    return null;
  }
  const reason = CANCEL_REASON_LABEL[row.cancelReason] ?? row.cancelReason;
  return row.cancelReason === 'other' && row.cancelNote
    ? `${reason}: ${row.cancelNote}`
    : reason;
};

export const BOOKING_STATUS_OPTIONS = (
  [
    'approved',
    'applied',
    'accepted',
    'needs_funding',
    'booked',
    'canceled',
  ] as BookingStatus[]
).map((value) => ({ value, label: BOOKING_STATUS_LABEL[value] }));

export const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((label, index) => ({ label, value: index + 1 }));

/** "$1,500.00", or "—" when unknown. */
export const money = (value?: number | null) =>
  value === null || value === undefined ? '—' : formatPrice(value);

/** Whole dollars for the dense Year Plan ("$1,500"). */
export const dollars = (value?: number | null) =>
  value === null || value === undefined ? '—' : formatPrice(value, '$0,0');

/** "Jan 2–10", "Jan 30–Feb 7". */
export const bookingDates = (row: Pick<BookingRow, 'endDate' | 'startDate'>) =>
  shortRange(row.startDate, row.endDate);

/** "Nov 15". */
export const shortDay = (day: string) => shortRange(day);

export const place = (row: Pick<BookingRow, 'city' | 'state'>) =>
  [row.city, row.state].filter(Boolean).join(', ');

/** The money columns of a row: Canceled shows carry none. */
export const nextPaymentText = (row: BookingRow) => {
  if (row.status === 'canceled') {
    return '—';
  }
  if (row.nextPayment) {
    return `${money(row.nextPayment.amount)} · ${shortRange(row.nextPayment.date)}`;
  }
  return row.balance && row.balance > 0 ? 'Not scheduled' : '—';
};

/** Booth payment kinds, as the Location form names them (short). */
export const PAYMENT_TRANSACTION_LABEL: Record<
  BookingPaymentTransaction,
  string
> = {
  coi: 'COI',
  misc: 'Misc',
  deposit: 'Deposit',
  fullPayment: 'Full',
  partialPayment: 'Partial',
  electricity: 'Electricity',
  applicationFee: 'Application fee',
};

export const PAYMENT_TRANSACTION_OPTIONS = (
  [
    'deposit',
    'partialPayment',
    'fullPayment',
    'applicationFee',
    'electricity',
    'coi',
    'misc',
  ] as BookingPaymentTransaction[]
).map((value) => ({ value, label: PAYMENT_TRANSACTION_LABEL[value] }));

/** Booth rent counts against the booth cost; Electricity, COI, Misc don't. */
export const isRentTransaction = (
  transaction?: BookingPaymentTransaction | null
) =>
  transaction === 'deposit' ||
  transaction === 'fullPayment' ||
  transaction === 'partialPayment' ||
  transaction === 'applicationFee';

/** Dollars (number or typed text) as whole cents; null when not a number. */
export const toCents = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) : null;
};

/**
 * The plan builder's rows (the server checks the same rule): equal split
 * rounded down to the cent, the remainder on the last payment; first
 * Deposit, last Full, the rest Partial (one payment = Full).
 */
export const splitPlan = (totalCents: number, days: string[]) => {
  const count = days.length;
  const each = Math.floor(totalCents / count);
  return days.map((dueDate, index) => ({
    dueDate,
    amountCents: index === count - 1 ? totalCents - each * (count - 1) : each,
    transaction: (count === 1 || index === count - 1
      ? 'fullPayment'
      : index === 0
        ? 'deposit'
        : 'partialPayment') as BookingPaymentTransaction,
  }));
};

/** Today in New York (the company's working day), as 'YYYY-MM-DD'. */
export const newYorkToday = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    year: 'numeric',
    month: '2-digit',
    timeZone: 'America/New_York',
  }).format(now);

/** "October 2026" for a 'YYYY-MM' month. */
export const monthName = (month: string) => {
  const [year, number] = month.split('-').map(Number);
  return `${MONTH_OPTIONS[number - 1]?.label ?? ''} ${year}`;
};

/** History lines of the booking panel. */
export const activityText = (
  type: string,
  payload: Record<string, unknown> | null
) => {
  const label = (value: unknown) =>
    BOOKING_STATUS_LABEL[value as BookingStatus] ?? String(value ?? '');
  switch (type) {
    case 'created':
      return 'Added to the list';
    case 'copied':
      return `Copied from ${payload?.fromYear ?? 'last year'}`;
    case 'imported':
      return payload?.linkedLocation
        ? 'Added from the 2027 booking sheet, linked to its Location'
        : 'Added from the 2027 booking sheet';
    case 'edited':
      return 'Details changed';
    case 'rep_assigned':
      return `${payload?.repName ?? 'A rep'} assigned`;
    case 'rep_cleared':
      return 'Rep removed';
    case 'priority_moved':
      return 'Moved in the queue';
    case 'status_changed': {
      const reason = cancelReasonText({
        cancelNote: (payload?.note as string | null) ?? null,
        cancelReason: (payload?.reason as BookingCancelReason) ?? null,
      });
      if (reason && payload?.from === payload?.to) {
        return `Cancel reason: ${reason}`;
      }
      const line = `${label(payload?.from)} → ${label(payload?.to)}`;
      return reason ? `${line} · ${reason}` : line;
    }
    case 'location_created':
      return `Booked: Location "${payload?.name ?? ''}" created`;
    case 'location_linked':
      return `Booked: linked to Location "${payload?.name ?? ''}"`;
    case 'payment_scheduled':
      return `Payment of ${money(Number(payload?.amount))} scheduled`;
    case 'payment_recorded':
      return `Payment of ${money(Number(payload?.amount))} recorded`;
    case 'payment_marked_paid':
      return `${money(Number(payload?.amount))} marked paid`;
    case 'payment_cashed':
      return 'Payment marked cashed';
    case 'plan_saved':
      return `Payment plan saved (${payload?.count ?? ''} payments, ${money(
        Number(payload?.total)
      )})`;
    default:
      return type;
  }
};
